import { createContext, useContext, useState, useCallback } from 'react';
import ContactFormModal from '@components/contact/ContactFormModal';

/**
 * ContactModalContext — global contact modal provider
 *
 * Allows any page to open the ContactFormModal with:
 *   const { openContact } = useContactModal();
 *   openContact('request-availability', { sku: '7329-3678-01', productName: 'Button Bit' });
 *
 * The modal renders once at the App level (inside ContactModalProvider)
 * and is controlled by this context — no prop drilling needed.
 *
 * Contextual triggers per live app (LIVE-APP-CAPTURE-2026-05-21.md §7e):
 *   - ProductDetailPage → request-order, request-availability, product-sellability, request-alternate
 *   - OrderDetailPage   → order-issue, return-request
 *   - ToolFinder         → missing-rig, missing-drill
 *   - Cart               → request-availability
 *   - ContactPage        → all 11 topics (fallback)
 */
const ContactModalContext = createContext(null);

export function ContactModalProvider({ children }) {
  const [isOpen, setIsOpen] = useState(false);
  const [topicId, setTopicId] = useState(null);
  const [context, setContext] = useState({});

  const openContact = useCallback((topic, ctx = {}) => {
    setTopicId(topic);
    setContext(ctx);
    setIsOpen(true);
  }, []);

  const closeContact = useCallback(() => {
    setIsOpen(false);
    // Delay clearing to allow close animation
    setTimeout(() => {
      setTopicId(null);
      setContext({});
    }, 300);
  }, []);

  return (
    <ContactModalContext.Provider value={{ openContact, closeContact, isOpen }}>
      {children}
      <ContactFormModal
        isOpen={isOpen}
        onClose={closeContact}
        topicId={topicId}
        context={context}
      />
    </ContactModalContext.Provider>
  );
}

export function useContactModal() {
  const ctx = useContext(ContactModalContext);
  if (!ctx) throw new Error('useContactModal must be used within ContactModalProvider');
  return ctx;
}

export default ContactModalContext;
